import type { ClientMessage, ServerMessage } from "./signalMessages";

/** Клиент сводника в браузере: JSON поверх WebSocket, ожидание нужного сообщения с тайм-аутом. */
export class SignalClient {
  private readonly listeners = new Set<(message: ServerMessage) => void>();

  private constructor(private readonly socket: WebSocket) {
    socket.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (typeof event.data !== "string") return;
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      for (const listener of this.listeners) listener(message);
    });
  }

  static open(url: string, timeoutMs = 8000): Promise<SignalClient> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(url);
      const timer = setTimeout(() => {
        socket.close();
        reject(new Error("Сводник не отвечает"));
      }, timeoutMs);
      socket.addEventListener("open", () => {
        clearTimeout(timer);
        resolve(new SignalClient(socket));
      });
      socket.addEventListener("error", () => {
        clearTimeout(timer);
        reject(new Error("Нет связи со сводником"));
      });
    });
  }

  send(message: ClientMessage): void {
    if (this.socket.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  on(listener: (message: ServerMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Дождаться сообщения, подходящего под условие; ошибка сводника — исключение с понятным текстом. */
  wait<T extends ServerMessage>(match: (message: ServerMessage) => message is T, timeoutMs: number): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        stop();
        reject(new Error("Соперник не пришёл вовремя"));
      }, timeoutMs);
      const stop = this.on((message) => {
        if (message.t === "error") {
          clearTimeout(timer);
          stop();
          reject(new Error(SIGNAL_ERRORS[message.reason]));
        } else if (match(message)) {
          clearTimeout(timer);
          stop();
          resolve(message);
        }
      });
    });
  }

  close(): void {
    this.socket.close();
  }
}

export const SIGNAL_ERRORS: Readonly<Record<"full" | "missing" | "bad" | "rate", string>> = {
  full: "Комната занята: в ней уже двое",
  missing: "Комната закрыта — создайте новую ссылку",
  bad: "Сводник не понял запрос",
  rate: "Слишком много запросов, подождите",
};
